import { FunctionComponent, useMemo } from 'react';
import { useSetAtom } from 'jotai';
import { useAuth } from '../../services/auth0';
import { switchTenantVisibleState } from '../../store/global.store';
import { getFirstLetter } from '../../utils';
import { MBox, MFlex, MText, MTooltip } from '../Monetize';

const AppMenuTenantSwitch: FunctionComponent<any> = ({ isOverlay }: any) => {
  const { currentTenant, getTenantIdsFromAccessToken } = useAuth();
  const toggleSwitchTenantModal = useSetAtom(switchTenantVisibleState);

  const hasMultipleTenants = useMemo(
    () => getTenantIdsFromAccessToken().length > 1,
    [getTenantIdsFromAccessToken],
  );

  const onOpen = () => {
    toggleSwitchTenantModal(true);
  };

  const isTest = !!currentTenant?.testTenant;

  // Test tenants get a loud orange badge — it's a real safety signal so users
  // know they're not in production. Live tenants get a quiet neutral tile with
  // the tenant initial; "Live" wordmark removed because it told users nothing
  // and dominated the corner.
  const tileProps = isTest
    ? {
        bg: 'tOrange.input',
        color: 'tOrange.base',
        _groupHover: { bg: 'tOrange.base', color: 'white' },
      }
    : {
        bg: 'tIndigo.base',
        color: 'white',
      };

  const tileLabel = isTest
    ? 'Test'
    : getFirstLetter(currentTenant?.name || '?');

  const tooltipLabel = hasMultipleTenants
    ? `${currentTenant?.name || ''} — switch tenant`
    : currentTenant?.name || 'Tenant';

  return (
    <MFlex
      py={1}
      px={isOverlay ? 0 : 4}
      justifyContent={isOverlay ? 'center' : 'flex-start'}
    >
      <MTooltip label={tooltipLabel} placement="bottom-end">
        <MFlex
          asChild
          aria-label={tooltipLabel}
          alignItems="center"
          cursor="pointer"
          onClick={onOpen}
          data-testid="tenant-modal-btn"
          bg="transparent"
          border="none"
          borderRadius="md"
          textAlign="left"
          p={1}
          w={isOverlay ? 'auto' : '100%'}
          role="group"
          className="group"
          _focusVisible={{
            outline: 'none',
            boxShadow: 'focus',
          }}
        >
          <button type="button">
            {currentTenant && (
              <MFlex
                borderRadius="md"
                w="8"
                h="8"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                {...tileProps}
              >
                <MText color="inherit" fontSize="xs" fontWeight="bold">
                  {tileLabel}
                </MText>
              </MFlex>
            )}

            {!isOverlay && (
              <MBox marginLeft={3} maxW="8rem" overflow="hidden">
                <MText
                  fontSize="xs"
                  fontWeight={600}
                  truncate
                  color="tIndigo.base"
                >
                  {currentTenant?.name}
                </MText>
                {hasMultipleTenants && (
                  <MText fontSize="xs" color="tIndigo.base" truncate>
                    Switch tenant
                  </MText>
                )}
              </MBox>
            )}
          </button>
        </MFlex>
      </MTooltip>
    </MFlex>
  );
};

export default AppMenuTenantSwitch;
